import React, { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search } from "lucide-react";

/**
 * Country prefixes — top 20 covering Romania + EU + diaspora destinations.
 * Format: { code: ISO 3166-1 alpha-2, name: Romanian display name, dial: '+XX', flag: emoji }
 * Romania ('RO') is the default.
 */
export const COUNTRY_CODES = [
  { code: "RO", name: "România", dial: "+40", flag: "🇷🇴" },
  { code: "MD", name: "Republica Moldova", dial: "+373", flag: "🇲🇩" },
  { code: "HU", name: "Ungaria", dial: "+36", flag: "🇭🇺" },
  { code: "BG", name: "Bulgaria", dial: "+359", flag: "🇧🇬" },
  { code: "AT", name: "Austria", dial: "+43", flag: "🇦🇹" },
  { code: "DE", name: "Germania", dial: "+49", flag: "🇩🇪" },
  { code: "FR", name: "Franța", dial: "+33", flag: "🇫🇷" },
  { code: "IT", name: "Italia", dial: "+39", flag: "🇮🇹" },
  { code: "ES", name: "Spania", dial: "+34", flag: "🇪🇸" },
  { code: "PT", name: "Portugalia", dial: "+351", flag: "🇵🇹" },
  { code: "GB", name: "Marea Britanie", dial: "+44", flag: "🇬🇧" },
  { code: "IE", name: "Irlanda", dial: "+353", flag: "🇮🇪" },
  { code: "NL", name: "Olanda", dial: "+31", flag: "🇳🇱" },
  { code: "BE", name: "Belgia", dial: "+32", flag: "🇧🇪" },
  { code: "CH", name: "Elveția", dial: "+41", flag: "🇨🇭" },
  { code: "PL", name: "Polonia", dial: "+48", flag: "🇵🇱" },
  { code: "CZ", name: "Cehia", dial: "+420", flag: "🇨🇿" },
  { code: "SK", name: "Slovacia", dial: "+421", flag: "🇸🇰" },
  { code: "GR", name: "Grecia", dial: "+30", flag: "🇬🇷" },
  { code: "TR", name: "Turcia", dial: "+90", flag: "🇹🇷" },
  { code: "US", name: "Statele Unite", dial: "+1", flag: "🇺🇸" },
  { code: "CA", name: "Canada", dial: "+1", flag: "🇨🇦" },
];

const DIAL_BY_CODE = Object.fromEntries(COUNTRY_CODES.map((c) => [c.code, c.dial]));

/**
 * Split an E.164-ish phone string into { dial, local } using the longest
 * matching prefix from COUNTRY_CODES. Falls back to RO default if no prefix
 * matches (i.e. user typed a plain local number).
 */
export function splitInternationalPhone(value) {
  const raw = (value || "").trim();
  if (!raw) return { code: "RO", local: "" };
  // Normalize: keep digits and leading +
  const cleaned = raw.replace(/[^\d+]/g, "");
  // Sort dials by length desc so '+373' wins over '+3'
  const sorted = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);
  for (const c of sorted) {
    if (cleaned.startsWith(c.dial)) {
      return { code: c.code, local: cleaned.slice(c.dial.length) };
    }
  }
  // 00... → +... fallback
  if (cleaned.startsWith("00")) {
    const withPlus = "+" + cleaned.slice(2);
    for (const c of sorted) {
      if (withPlus.startsWith(c.dial)) {
        return { code: c.code, local: withPlus.slice(c.dial.length) };
      }
    }
  }
  // Local number → default RO, strip leading 0
  return { code: "RO", local: cleaned.replace(/^0/, "") };
}

/**
 * Concatenate the country dial + local part into an E.164-ish string.
 * We always return the dial prefix — even if the local part is empty —
 * so the parent's state reflects the picked country and `splitInternationalPhone`
 * can round-trip back to the correct flag.
 */
export function joinInternationalPhone(code, local) {
  const dial = DIAL_BY_CODE[code] || "+40";
  const clean = (local || "").replace(/[^\d]/g, "");
  return `${dial}${clean}`;
}

/**
 * Drop-in international phone input.
 *
 * Props:
 *   value: the full E.164 string (e.g. "+40712345678")
 *   onChange(nextValue): called whenever the user changes either part
 *   placeholder: shown in the local-number field
 *   testid: data-testid root; sub-elements get `${testid}-country`, etc.
 *   inputClassName: tailwind classes for the local number <input>
 *   disabled: optional
 */
export default function PhoneInputIntl({
  value,
  onChange,
  placeholder = "7xx xxx xxx",
  testid = "phone-intl",
  inputClassName = "",
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapperRef = useRef(null);

  const { code, local } = useMemo(() => splitInternationalPhone(value), [value]);
  const selected = COUNTRY_CODES.find((c) => c.code === code) || COUNTRY_CODES[0];

  // Close dropdown on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return COUNTRY_CODES;
    return COUNTRY_CODES.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.code.toLowerCase().includes(q),
    );
  }, [search]);

  const handleSelect = (nextCode) => {
    onChange(joinInternationalPhone(nextCode, local));
    setOpen(false);
    setSearch("");
  };

  const handleLocalChange = (e) => {
    const next = e.target.value.replace(/[^\d\s.-]/g, "");
    onChange(joinInternationalPhone(code, next));
  };

  return (
    <div
      ref={wrapperRef}
      className="relative flex w-full"
      data-testid={testid}
    >
      <button
        type="button"
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        className="flex items-center gap-1.5 px-2.5 rounded-l-md border border-r-0 border-white/10 bg-zinc-900/60 hover:bg-zinc-900 text-white text-sm transition-colors min-w-[88px]"
        data-testid={`${testid}-country`}
        aria-label="Selectează țara"
      >
        <span className="text-lg leading-none" aria-hidden="true">
          {selected.flag}
        </span>
        <span className="text-zinc-300 text-xs font-mono">{selected.dial}</span>
        <ChevronDown size={12} className="text-zinc-500" />
      </button>
      <input
        type="tel"
        inputMode="tel"
        value={local}
        onChange={handleLocalChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`flex-1 rounded-r-md border border-white/10 bg-zinc-950/50 text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500/30 ${inputClassName}`}
        data-testid={`${testid}-local`}
        autoComplete="tel-national"
      />

      {open && (
        <div
          className="absolute top-full left-0 mt-1 w-72 max-h-72 overflow-hidden rounded-md border border-orange-500/20 bg-zinc-950 shadow-xl z-30"
          data-testid={`${testid}-dropdown`}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5 bg-zinc-900/60">
            <Search size={12} className="text-zinc-500" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Caută țară..."
              className="bg-transparent flex-1 text-sm text-white placeholder:text-zinc-500 outline-none"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-xs text-zinc-500 text-center italic">
                Nicio țară găsită
              </div>
            ) : (
              filtered.map((c) => (
                <button
                  type="button"
                  key={c.code}
                  onClick={() => handleSelect(c.code)}
                  className={`w-full text-left px-3 py-2 hover:bg-orange-500/10 flex items-center gap-2.5 transition-colors ${
                    c.code === code ? "bg-orange-500/10" : ""
                  }`}
                  data-testid={`${testid}-option-${c.code}`}
                >
                  <span className="text-lg" aria-hidden="true">
                    {c.flag}
                  </span>
                  <span className="flex-1 text-sm text-white truncate">
                    {c.name}
                  </span>
                  <span className="text-xs text-zinc-500 font-mono">{c.dial}</span>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
